import { ClientFetch } from '../../fetch/ClientFetch';
import { HTTP_METHODS, buildBrandLanguageCountryParam, OS_MAP } from 'utils';
import type { GetSoftwareDownloadUrlParams } from '../../../types/downloadSoftware';

/**
 * Gets the software download URL from PSI service based on operating system.
 *
 * This function calls the PSI endpoint `download.software.getDownloadUrl` with the
 * user's operating system and brand-language-country parameter, then builds a complete
 * download URL with tracking parameters (Google Click ID, GA/Jentis client ID).
 *
 * This function is client/server agnostic - all environment-specific data must be
 * passed as parameters.
 *
 * Based on Gatsby implementation:
 * - packages/core/src/services/getSoftwareDownloadUrl.ts
 * - packages/core/src/helpers/hooks/handleSoftwareDownload.ts
 *
 * @param params - Parameters including OS, locale, country URL, and tracking data
 * @returns Promise resolving to the download URL with tracking parameters
 *
 * @example
 * ```typescript
 * const downloadUrl = await getSoftwareDownloadUrl({
 *   operatingSystem: 'Windows',
 *   locale: 'de-DE',
 *   countryUrl: 'https://www.pixum.de',
 *   googleClickId: 'xyz123',
 *   clientId: 'abc456'
 * });
 * // Returns: 'https://www.pixum.de/path/to/software.exe?clickId=xyz123&clientId=abc456'
 * ```
 */
export async function getSoftwareDownloadUrl(params: GetSoftwareDownloadUrlParams): Promise<string> {
  const { operatingSystem, locale, countryUrl, googleClickId, clientId } = params;

  // Map OS name to PSI code (e.g., 'Windows' -> 'win')
  const osCode = OS_MAP[operatingSystem];

  if (!osCode) {
    throw new Error(`Unsupported operating system: ${operatingSystem}`);
  }

  // Build brand-language-country parameter (e.g., 'de-DE' -> '1,0,1')
  const clParam = buildBrandLanguageCountryParam(locale);

  // Call PSI service (same as Gatsby implementation)
  // Response is an array with URL as first element (based on Gatsby: data[0])
  const response = await new ClientFetch().makePsiRequest<unknown>(
    'download.software.getDownloadUrl',
    { os: osCode, cl: clParam },
    { method: HTTP_METHODS.GET }
  );

  // Validate response
  if (!Array.isArray(response) || typeof response[0] !== 'string') {
    throw new Error('Invalid PSI response format');
  }

  // Build full URL with country URL prefix
  let downloadUrl = `${countryUrl}${response[0]}`;

  // Add tracking parameters if provided
  if (googleClickId) {
    downloadUrl = `${downloadUrl}&clickId=${googleClickId}`;
  }

  if (clientId) {
    downloadUrl = `${downloadUrl}&clientId=${clientId}`;
  }

  return downloadUrl;
}
